import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global, useMenuStore } from "@shared/libs/Global";
import SearchPanel from "@widgets/SearchPanel";
import SnTree from "@shared/components/GridV2/SnTree";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "@widgets/CommonDialog";
import { ColDef } from "ag-grid-community";
import SnButton from "@shared/components/SnButton";
import { dateToString } from "@shared/utils/DateUtils";
import CommonSpinner from "@widgets/CommonSpinner";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_MAS_ORG_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    tree: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    tree: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    tree: [],
  });
  const [loading, setLoading] = useState(false);
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          tree: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    try {
      const modifiedRows = gridRefs.current["tree"].current.getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const result = await onSave();
          if (!result) return;
        }
      }

      const param = {
        id: "sn_mas_org_reg_004_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["tree"].current.resetUpdate();
      const data: any = await fnGetDataSet(param);
      setDataSources({
        tree: data[0],
      });
    } catch (error) {}
  };

  const onInsert = async () => {
    try {
      const selectedNodes = gridRefs.current["tree"].current.getSelectedNodes();
      const selectedNode =
        selectedNodes && selectedNodes.length > 0 ? selectedNodes[0] : null;

      const formData = searchRef.current.getFormdata();

      // 신규 행 기본 데이터
      const newRow: RowData = {
        cd_firm: userInfo.cd_firm,
        cd_biz: formData.cd_biz,
        yn_end: "Y",
        yn_use: "Y",
        dt_start: dateToString(new Date()),
      };

      if (!selectedNode) {
        // parent 없음 → 루트에 추가
        newRow.cd_dept_p = null;
        newRow.no_seq_sort = 1;
        gridRefs.current["tree"].current?.addRow(newRow, null);
        return;
      }

      // 선택된 노드가 있는 경우
      const parentNode = selectedNode.parent;

      // 같은 레벨의 형제 노드들
      const siblings = parentNode
        ? parentNode.childrenAfterGroup || []
        : gridRefs.current["tree"].current?.getDisplayedRowAtIndex(0)?.parent
            ?.childrenAfterGroup || [];

      // 형제들 중 no_seq_sort 최대값 계산
      const maxNoLine =
        siblings.length > 0
          ? Math.max(
              ...siblings.map((child: any) => child.data.no_seq_sort || 0)
            )
          : 0;

      newRow.cd_dept_p = parentNode.data ? parentNode.data.cd_dept : null;
      newRow.no_seq_sort = maxNoLine + 1;

      gridRefs.current["tree"].current?.addRow(newRow, parentNode);
    } catch (error) {
      console.error(error);
    }
  };

  const onDelete = () => {
    gridRefs.current["tree"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["tree"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["tree"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const changes = gridRefs.current["tree"].current.getChange();
      if (changes.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const insertList = changes.insert;
      for (const row of insertList) {
        const cd_dept = row.cd_dept;
        if (cd_dept?.includes("temp-")) {
          await showDialog(
            "alter",
            "temp 부서코드가 존재합니다. 부서코드를 입력해주세요."
          );
          return false;
        }
      }

      const ok = await fnSave("SN_MAS_ORG_REG_004_SAVE_01", {
        mas_dept: changes,
      });
      if (!ok) return false;

      gridRefs.current["tree"].current.resetUpdate();
      return true;
    } catch (error) {
      gridRefs.current["tree"].current.resetUpdate(true);
      return false;
    }
  };

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: "부서",
      field: "nm_dept",
      minWidth: 300,
      cellRendererParams: {
        suppressCount: true, // 하위 아이템 개수 숨기기
        suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
        suppressHoverHighlight: false,
      },
      rowDrag: true,
    };
  }, []);

  const getRowId = useCallback(
    (params: any) => String(params.data.cd_dept),
    []
  );

  const onGrid1BtnsClick = async (id: string) => {
    const api = gridRefs.current["tree"].current;
    if (!api) return;

    if (id === "btnOpen") {
      api.setExpandedAll(true);
    }
    if (id === "btnClose") {
      api.setExpandedAll(false);
    }
    if (id === "btnAddRow") {
      const selectedNodes = api.getSelectedNodes();
      const selectedNode =
        selectedNodes && selectedNodes.length > 0 ? selectedNodes[0] : null;

      if (!selectedNode) {
        await showDialog("alter", "상위 부서를 선택해주세요.");
        return;
      }

      if (
        !selectedNode.data?.cd_dept ||
        String(selectedNode.data.cd_dept).startsWith("temp-")
      ) {
        await showDialog("alter", "부서코드를 입력해주세요.");
        return;
      }

      const formData = searchRef.current.getFormdata();
      const selectedNodeChildren = selectedNode.childrenAfterGroup || [];
      const maxNoLine =
        selectedNodeChildren.length > 0
          ? Math.max(
              ...selectedNodeChildren.map(
                (child: any) => child.data.no_seq_sort || 0
              )
            )
          : 0;

      // 닫혀 있으면 열기
      if (!selectedNode.expanded) {
        selectedNode.setExpanded(true);
        api.onGroupExpandedOrCollapsed();
      }

      // 신규행 구성
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: formData.cd_biz,
        cd_dept_p: selectedNode.data.cd_dept,
        no_seq_sort: maxNoLine + 1,
        yn_end: "Y",
        yn_use: "Y",
        dt_start: dateToString(new Date()),
      };

      api.addRow(newRow, selectedNode);
    }
    if (id === "btnImport") {
      setLoading(true);
      try{
        const result= await callApi("SN_MAS_ORG_REG_004_btnImport", {
          // cd_firm은 SNOTES 회사코드(연동설정 조회용), coCd는 더존 회사코드로 서로 다른 체계다.
          cd_firm: userInfo.cd_firm,
        });

        //완료/실패 안내창은 callApi가 백엔드 message로 띄운다.
        if(result?.status) await onSearch();
      } catch(error: any) {
      } finally{
        //성공·실패·중간 return무엇이든 스피너는 반드시 닫는다.
        setLoading(false);
      }
    }
  };

  const onRowDataReady = () => {
    gridRefs.current["tree"].current.selectAndFocusRow(0);
  };

  const grids = useMemo(
    () => ({
      tree: {
        columns: columns.tree.map((col: any) => {
          if (col.cd_column === "cd_dept") {
            return {
              ...col,

              valueFormatter: (params: any) => {
                const value = params.value;

                if (typeof value === "string" && value.startsWith("temp-")) {
                  return "";
                }

                return value;
              },
            };
          }

          return col;
        }),

        dataSource: dataSources.tree,

        events: {
          onRowDataReady: onRowDataReady,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        />
      </div>

      <div style={{ flex: "1 1 auto", display: "flex", gap: "10px" }}>
        <div style={{ flex: "1 1 100%", overflow: "auto" }}>
          <SnTree
            ref={gridRefs.current["tree"]}
            columnConfig={grids.tree.columns}
            rowData={grids.tree.dataSource}
            etcProps={{
              isEditable: true,
              rowSelection: "multiple",
              title: "상세정보",
            }}
            header={
              <div>
                <SnButton
                  text="전체전개"
                  onClick={() => onGrid1BtnsClick("btnOpen")}
                />
                <SnButton
                  text="전체닫기"
                  onClick={() => onGrid1BtnsClick("btnClose")}
                />
                <SnButton
                  text="하위레벨추가"
                  onClick={() => onGrid1BtnsClick("btnAddRow")}
                />
                {/*더존(AM10)연동 환경에서만 사원정보 가져오기를 노출한다. */}
                  {config?.MAS0001?.dc_ctrl === "AM10"&& (
                  <SnButton text="가져오기" onClick={() => onGrid1BtnsClick("btnImport")}
                  />)}
              </div>
            }
            events={grids.tree.events}
            treeProps={{
              treeDataParentIdField: "cd_dept_p",
              treeIdKey: "cd_dept",
              treeOrderKey: "no_seq_sort",
              groupDefaultExpanded: -1,
              autoGroupColumnDef: autoGroupColumnDef,
              getRowId: getRowId,
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default SN_MAS_ORG_REG_004;
